<template>
  <div
    v-if="skillDivePlan"
    :class="[
      'plan-skill-dive-item-card',
      asAddOns && 'plan-skill-dive-item-card--as-add-ons',
      isCyberMondayActive && 'plan-skill-dive-item-card--cyber-monday',
    ]"
  >
    <template v-if="!asAddOns">
      <a target="_blank" href="https://ine.com/dive" class="plan-skill-dive-item-card__logo">
        <img
          :src="skillDiveLogoBadge"
          alt="Skill Dive Experience"
          class="plan-skill-dive-item-card__logo-icon"
        />
      </a>
      <div class="plan-skill-dive-item-card__price">
        <div class="plan-skill-dive-item-card__price-amount">
          {{ roundPrice }}
          <span class="plan-skill-dive-item-card__price-amount--cents">{{ centsPrice }}</span>
        </div>
        <div class="plan-skill-dive-item-card__price-separator"></div>
        <div class="plan-skill-dive-item-card__price-explained">
          <div class="plan-skill-dive-item-card__price-explained-total-hours">
            {{ skillDivePlan.totalHours }} hours
          </div>
          <div class="plan-skill-dive-item-card__price-explained-expiration">
            {{ hoursExpiration }}
          </div>
        </div>
      </div>
      <div class="plan-skill-dive-item-card__la-add-ons">
        <h3 class="plan-skill-dive-item-card__title">Focus areas:</h3>
        <PlanSkillDiveLAItem
          v-for="addOn in skillDivePlan.addOns"
          :key="addOn.id"
          :plan="skillDivePlan"
          :item="addOn"
        />
      </div>
      <div class="plan-skill-dive-item-card__price-disclaimer">
        {{ priceDisclaimer }}
      </div>
    </template>
    <template v-if="asAddOns">
      <div class="plan-skill-dive-item-card__price">
        <div class="plan-skill-dive-item-card__price-amount-wrapper">
          <div class="plan-skill-dive-item-card__price-amount">
            {{ roundPrice }}
            <span>{{ centsPrice }}</span>
            <span class="plan-skill-dive-item-card__price-amount-year">/year</span>
          </div>
        </div>
        <div class="plan-skill-dive-item-card__price-separator"></div>
        <div class="plan-skill-dive-item-card__price-explained">
          <div class="plan-skill-dive-item-card__price-explained-total-hours">
            {{ skillDivePlan.totalHours }} hours
          </div>
          <div class="plan-skill-dive-item-card__price-explained-expiration">
            {{ hoursExpiration }}
          </div>
        </div>
      </div>
      <div class="plan-skill-dive-item-card__renewal-terms">
        Your INE subscription will automatically renew at the end of each subscription term unless
        you cancel your renewal prior to the end of said term. You must cancel your subscription
        renewal before the scheduled renewal date to avoid auto renewal and the associated renewal
        fee.
      </div>
      <div class="plan-skill-dive-item-card__price-disclaimer">
        {{ priceDisclaimer }}
      </div>
      <div class="plan-skill-dive-item-card__la-add-ons">
        <PlanSkillDiveLAItem
          v-for="addOn in skillDivePlan.addOns"
          :asAddOns="asAddOns"
          :key="addOn.id"
          :plan="skillDivePlan"
          :item="addOn"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import skillDiveLogo from '@/assets/images/hero-section/skill-dive/skill-dive-logo.png'
import PlanSkillDiveLAItem from '@/components/plan-item-card/PlanSkillDiveLAItem.vue'
import CONSTANTS from '@/constants'

const { PLANS } = CONSTANTS

export default {
  name: 'PlanSkillDiveItemCard',
  components: {
    PlanSkillDiveLAItem,
  },
  props: {
    asAddOns: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      skillDiveLogoBadge: skillDiveLogo,
    }
  },
  computed: {
    ...mapGetters({
      plans: 'products/plansByTerm',
      selectedPlan: 'shoppingCart/selectedPlan',
      selectedBundle: 'shoppingCart/selectedBundle',
      isBusinessUser: 'business/isBusinessUser',
      isBusinessPlanExpired: 'business/isBusinessPlanExpired',
      isCyberMondayActive: 'featureFlags/isCyberMondayActive',
    }),
    price() {
      let totalPrice = this.skillDivePlan.priceToShow
      if (this.isCyberMondayActive) {
        totalPrice = this.skillDivePlan.discountedPriceToShow
      }
      return totalPrice % 100 === 0 ? (totalPrice / 100).toFixed(0) : (totalPrice / 100).toFixed(2)
    },
    roundPrice() {
      const priceWithouCents = String(this.price).split('.')[0]
      return `$${priceWithouCents}`
    },
    centsPrice() {
      const priceCents = String(this.price).split('.')[1]
      return priceCents ? `.${priceCents}` : ''
    },

    priceDisclaimer() {
      const addOn = this.skillDivePlan?.addOns[0]
      const price = this.price
      return `$${price} for one focus area. $${
        addOn.price_cents / 100
      } for each additional focus area.`
    },
    hoursExpiration() {
      return 'Hours expire 1 year after purchase'
    },
    priceUserSaves() {
      return `$${(this.skillDivePlan.priceToShow - this.skillDivePlan.discountedPriceToShow) / 100}`
    },
    skillDivePlan() {
      return this.plans.yearly.find(plan => plan.merchant_id === PLANS.SKILL_DIVE_ANNUAL)
    },
  },
  methods: {
    ...mapMutations({
      setIsSignInModalShown: 'app/IS_SIGN_IN_MODAL_SHOWN_UPDATED',
    }),
    handleOpenModal() {
      this.setIsSignInModalShown(true)
    },
  },
}
</script>
<style lang="scss" scoped>
.plan-skill-dive-item-card {
  border-radius: 20px;
  background: linear-gradient(292deg, rgba(61, 36, 165, 0) 39.64%, #3d24a5 93.92%) #111111;
  padding: 20px;
  display: flex;
  flex-flow: column;
  gap: 20px;
  position: relative;
  @include mobile {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 20px;
    padding: 40px;
  }
  @include desktop {
    grid-template-rows: 1fr auto 1fr;
  }
  &--cyber-monday {
    border-radius: 0px 0px 10px 10px;
  }
  &--as-add-ons {
    padding: 0;
    display: flex;
    flex-flow: column;
    background: none;
    gap: 20px;
    @include mobile {
      padding: 20px;
      padding-top: 0;
    }
  }
  &__logo {
    &-icon {
      width: 200px;
      @include desktop {
        width: 300px;
      }
    }
  }
  &__price {
    display: flex;
    gap: 10px;
    align-items: center;
    @include mobile {
      align-self: baseline;
    }
    &-amount {
      &--cents {
        font-size: 35px;
      }
      display: flex;
      align-items: flex-start;
      gap: 5px;

      font-family: var(--font-medium);
      font-size: rem(54px);
      font-style: normal;
      font-weight: 700;
      position: relative;
      margin-right: 5px;

      &-year {
        color: var(--dusty-gray);
        font-feature-settings: 'clig' off, 'liga' off;
        font-family: var(--font-regular);
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 450;
        line-height: 0.875rem;
      }
      &-wrapper {
        &--neon-badge {
          display: flex;
          gap: 10px;
          .plan-skill-dive-item-card__price-amount {
            margin-right: 0;
            @include mobile {
              margin-right: 20px;
            }
          }
          &-addon {
            display: flex;
            gap: 10px;
            flex-flow: column-reverse;
            position: relative;
            margin-bottom: 40px;
            @include mobile {
              flex-flow: row;
              margin-bottom: 0;
            }
            .plan-skill-dive-item-card__price-amount {
              margin-right: 0;
            }
          }
        }
      }
    }
    &-cents {
      font-size: rem(27px);
      position: absolute;
      top: -7px;
    }
    &-separator {
      height: 65px;
      width: 1px;
      background: #2bbab2;
    }
    &-explained {
      &-total-hours {
        font-size: rem(20px);
        line-height: rem(26px);
        font-style: normal;
        font-weight: 700;
      }
      &-expiration {
        color: var(--dusty-gray);
        font-size: rem(12px);
        line-height: rem(14px);
        font-style: normal;
        font-weight: 450;
        width: 110%;
      }
    }
    &-disclaimer {
      font-size: rem(16px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(23px);
      @include mobile {
        align-self: flex-end;
      }
    }
  }
  &__la-add-ons {
    display: flex;
    flex-flow: column;
    gap: 10px;
    min-width: 210px;
    @include mobile {
      min-width: 230px;
      grid-area: 1/2/4/2;
    }
  }
  &__title {
    color: var(--white);
    font-size: rem(16px);
    font-weight: 500;
    line-height: rem(23px);
    margin: 0;
  }
  &__renewal-terms {
    color: var(--dusty-gray);
    font-size: rem(12px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(14px);
  }
}
</style>
